import React, { Component } from 'react';
import { connect } from 'react-redux';

import { getCategories, getCategory } from '../../categories/actions/CategoryActions';
import { getSuppliers } from '../../suppliers/actions/SupplierActions';
import { getUnits } from '../../units/actions/UnitActions';
import { createProduct } from '../actions/ProductActions';

import Request from '../../../util/Api';
import Config from '../../../util/Configuration';
import Cookie from 'react-cookies';

import { Button, Heading, Pane, Spinner, toaster, EyeOpenIcon } from 'evergreen-ui';
import InfoBlock from '../../common/InfoBlock/InfoBlock';

import { Link } from 'react-router-dom';
import ProductForm from '../components/ProductForm';
import CatalogDetailModal from '../../catalog/components/CatalogDetailModal';

import Configuration from '../Config';

const validation = Configuration.Validations.product;
const defaultValues = Configuration.Defaults.createProduct;
const mapValidationErrors = Configuration.ErrorHandler.mapValidationErrors;

// S3 bucket URL for catalog images
const CATALOG_IMAGE_BUCKET = process.env.REACT_APP_CATALOG_IMAGE_BUCKET || 'https://krit-content.ams3.cdn.digitaloceanspaces.com/catalog-images';

class CreateProduct extends Component {
    state = {
        initialValues: defaultValues,
        loading: false,
        catalogItem: null,
        catalogModalOpen: false,
        refreshingCatalogItem: false,
        syncingCatalogImages: false
    };

    async componentDidMount() {
        this.props.dispatch(getUnits());
        this.props.dispatch(getCategories());
        this.props.dispatch(getSuppliers());

        // Check for catalogId in URL query params
        const params = new URLSearchParams(this.props.location?.search || window.location.search);
        const catalogId = params.get('catalogId');

        if (catalogId) {
            await this.loadCatalogItem(catalogId);
        }
    }

    loadCatalogItem = async (catalogId) => {
        this.setState({ loading: true });

        try {
            const result = await Request.get(`/v2/catalog/${catalogId}`, {}, {});

            if (result && result.id) {
                const catalogItem = result;
                
                // Generate slug from name
                const slug = (catalogItem.name || 'produs')
                    .toLowerCase()
                    .replace(/[^a-z0-9\s-]/g, '')
                    .replace(/\s+/g, '-')
                    .substring(0, 100) || 'produs-nou';

                // Get synced images and convert to the format expected by ProductForm
                const syncedImages = (catalogItem.images || [])
                    .filter(img => img.synced === 1 && img.imageUrl)
                    .map((img, index) => ({
                        // Use the original image URL from S3 for display and processing
                        data: `${CATALOG_IMAGE_BUCKET}/original_${img.imageUrl}.jpg`,
                        position: index,
                        isUrl: true // Flag to indicate this is a URL, not base64
                    }));

                // Map catalog item to product form initial values
                const initialValues = {
                    ...defaultValues,
                    title: catalogItem.name || '',
                    description: catalogItem.description || catalogItem.name || '',
                    metaTitle: catalogItem.name || '',
                    metaDescription: (catalogItem.description || catalogItem.name || '').substring(0, 160),
                    barcode: catalogItem.gtin || catalogItem.itemId || `CAT-${catalogItem.id}`,
                    sku: catalogItem.itemId || '',
                    slug: slug,
                    service: catalogItem.type === 'service',
                    images: syncedImages
                };

                this.setState({ 
                    initialValues, 
                    catalogItem,
                    loading: false 
                });

                toaster.notify(`Date preîncărcate din catalog: ${catalogItem.name}`);
            } else {
                toaster.warning('Articolul din catalog nu a putut fi găsit');
                this.setState({ loading: false });
            }
        } catch (error) {
            console.error('Error loading catalog item:', error);
            toaster.danger('Eroare la încărcarea articolului din catalog');
            this.setState({ loading: false });
        }
    };

    handleSave = (values) => {
        this.props.dispatch(createProduct(values));
    }

    openCatalogModal = () => {
        this.setState({ catalogModalOpen: true });
    }

    closeCatalogModal = () => {
        this.setState({ catalogModalOpen: false });
    }

    refreshCatalogItem = async () => {
        if (!this.state.catalogItem?.id) return;
        
        this.setState({ refreshingCatalogItem: true });
        
        try {
            const result = await Request.get(`/v2/catalog/${this.state.catalogItem.id}`, {}, {});
            
            if (result && result.id) {
                this.setState({ 
                    catalogItem: result,
                    refreshingCatalogItem: false 
                });
                toaster.success('Date catalog actualizate');
            } else {
                this.setState({ refreshingCatalogItem: false });
                toaster.danger('Nu s-au putut actualiza datele');
            }
        } catch (error) {
            console.error('Error refreshing catalog item:', error);
            this.setState({ refreshingCatalogItem: false });
            toaster.danger('Eroare la actualizarea datelor');
        }
    };

    syncCatalogImages = async () => {
        if (!this.state.catalogItem?.id) return;
        
        this.setState({ syncingCatalogImages: true });
        
        try {
            const response = await fetch(`${Config.host}/v2/catalog/${this.state.catalogItem.id}/sync-images`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'session': Cookie.load('nax-session')
                }
            });
            
            const result = await response.json();
            
            if (response.ok) {
                toaster.success(result.message || 'Imagini sincronizate cu succes');
                // Refresh the catalog item to show newly synced images
                await this.refreshCatalogItem();
            } else {
                toaster.danger(result.error || 'Eroare la sincronizare');
            }
        } catch (error) {
            toaster.danger('Eroare la sincronizare: ' + error.message);
        }
        
        this.setState({ syncingCatalogImages: false });
    };

    render() {
        if (this.state.loading) {
            return (
                <Pane padding={16} display="flex" alignItems="center" justifyContent="center" height={400}>
                    <Spinner />
                </Pane>
            );
        }

        return (
            <Pane padding={16} paddingTop={0}>
                <Pane display="flex">
                    <Pane>
                        <Link to="/products">
                            <Button iconBefore="arrow-left" intent="none">
                                Inapoi la lista produse
                            </Button>
                        </Link>
                    </Pane>
                </Pane>

                <Pane flex={1} background="tint1" elevation={1} padding={16} marginTop={16} display="flex" alignItems="center" justifyContent="space-between">
                    <Heading size={600}>
                        Produs nou
                        {this.state.catalogItem && (
                            <span style={{ fontWeight: 'normal', fontSize: '14px', marginLeft: '10px', color: '#666' }}>
                                (din catalog: {this.state.catalogItem.name})
                            </span>
                        )}
                    </Heading>
                    {this.state.catalogItem && (
                        <Button 
                            iconBefore={EyeOpenIcon}
                            onClick={this.openCatalogModal}
                        >
                            Vezi datele din catalog
                        </Button>
                    )}
                </Pane>

                <Pane display="flex" justifyContent="space-between" flex={1} marginTop={16}>
                    <InfoBlock flex="1">
                        <ProductForm
                            isEdit={false}
                            initialValues={this.state.initialValues}
                            validationSchema={validation}
                            categories={this.props.categories}
                            suppliers={this.props.suppliers}
                            units={this.props.units}
                            onSave={this.handleSave}
                            errors={this.props.errors}
                            onCategoryChange={(id) => { this.props.dispatch(getCategory(id)) }}
                            category={this.props.selectedCategory}/>
                    </InfoBlock>
                </Pane>

                {/* Catalog Detail Modal */}
                <CatalogDetailModal
                    isShown={this.state.catalogModalOpen}
                    item={this.state.catalogItem}
                    onClose={this.closeCatalogModal}
                    showCreateButton={false}
                    onRefresh={this.refreshCatalogItem}
                    refreshing={this.state.refreshingCatalogItem}
                    onSyncImages={this.syncCatalogImages}
                    syncing={this.state.syncingCatalogImages}
                />
            </Pane>
        )
    }
}

function mapStateToProps(state, ownProps) {
    return {
        errors: state.products.errors.type === 'validation' ? mapValidationErrors(state.products.errors.fields): [],
        selectedCategory: state.categories.current,
        categories: state.categories.flatData,
        suppliers: state.suppliers.data,
        units: state.units.data,
        location: ownProps.location
    }
}

export default connect(mapStateToProps)(CreateProduct);
